<style lang="postcss">
	:root {
		--day-size: 2rem;
	}
	@media (max-width: 640px) {
		:root {
			--day-size: 2.1rem;
		}
	}
	.padding-normal {
		padding: 0.75rem;
	}
	.month,
	.day-week,
	.year {
		font-size: 90%;
		font-weight: 500;
	}
	.date-header .date-atom,
	.day-week {
		font-size: 90%;
		font-weight: 500;
	}
	.date-header .date-atom:hover {
		opacity: 1;
	}
	.day-week {
		text-align: center;
		width: calc(var(--day-size) + 0.1rem);
		aspect-ratio: 1 / 1;
		opacity: 0.65;
	}
	.day {
		font-size: 85%;
		font-weight: 500;
		text-align: center;
		width: calc(var(--day-size) + 0.1rem);
		aspect-ratio: 1 / 1;
	}
	.date-atom {
		display: flex;
		align-items: center;
		justify-content: center;
		opacity: 1;
		border-radius: 0px;
		font-weight: 400;
	}
	.date-atom:hover,
	.date-atom-darker:hover,
	.date-atom.active,
	.date-atom.highlight,
	.date-atom:active {
		opacity: 1;
		-webkit-text-stroke: 0.4px;
	}
	.calendar {
		position: relative;
		width: fit-content;
	}
	.calendar-transition {
		transition: opacity 100ms ease-in-out;
	}
	.date-header {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: space-between;
		overflow: hidden;
	}
	.datepicker-grid {
		padding-top: 0.5rem;
		display: grid;
		grid-template-columns: repeat(7, 1fr);
		text-transform: capitalize;
	}
	.year-selection,
	.month-selection {
		display: flex;
		align-items: center;
		justify-content: center;
		height: 100%;
		padding: 0.75em;
		overflow-y: auto;
	}
	.year-selection-grid,
	.month-selection-grid {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		margin: auto;
	}
	.calendar-grid {
		display: grid;
		grid-template-columns: repeat(7, 1fr);
	}
	.disabled,
	.disabled:hover {
		cursor: not-allowed !important;
		opacity: 0.2 !important;
	}
</style>

<script lang="ts">
	import { writable } from "svelte/store";
	import dayjs, { type Dayjs } from "dayjs";
	import calendar from "dayjs/plugin/calendar";
	import localizedFormat from "dayjs/plugin/localizedFormat";
	import utc from "dayjs/plugin/utc";
	import timezone from "dayjs/plugin/timezone";
	import isoWeek from "dayjs/plugin/isoWeek";
	import weekday from "dayjs/plugin/weekday";
	import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
	import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
	import isBetween from "dayjs/plugin/isBetween";
	import { fade, slide } from "$lib/transitions";
	import { tick } from "svelte";
	const localeModules = import.meta.glob(`/node_modules/dayjs/esm/locale/*.js`);

	import { createEventDispatcher } from "svelte";
	const dispatch = createEventDispatcher();

	// Load Day.js plugins
	dayjs.extend(utc);
	dayjs.extend(calendar);
	dayjs.extend(localizedFormat);
	dayjs.extend(isoWeek);
	dayjs.extend(weekday);
	dayjs.extend(isSameOrBefore);
	dayjs.extend(isSameOrAfter);
	dayjs.extend(isBetween);
	dayjs.extend(timezone);

	// Options
	export let min: string = "";
	export let max: string = "";
	export let highlightFrom: string = "";
	export let highlightTo: string = "";
	export let locale: string = "en";
	export let value: string = "";
	export let allowedDays: boolean[] = [
		true,
		true,
		true,
		true,
		true,
		true,
		true
	];
	let minDate = dayjs(min).isValid()
		? dayjs.tz(min, "UTC").utc()
		: dayjs().utc().subtract(100, "year");
	let maxDate = dayjs(max).isValid()
		? dayjs.tz(max, "UTC").utc()
		: dayjs().utc().add(100, "year");

	$: minDate = dayjs(min).isValid()
		? dayjs.tz(min, "UTC").utc()
		: dayjs().utc().subtract(100, "year");
	$: maxDate = dayjs(max).isValid()
		? dayjs.tz(max, "UTC").utc()
		: dayjs().utc().add(100, "year");
	$: firstMonth = minDate.format("MM-YYYY");
	$: lastMonth = maxDate.format("MM-YYYY");
	$: changeCurrentScreen(currentScreen);

	function shouldHighlight(day: Dayjs, from: string, to: string) {
		if (from && dayjs(from).isValid() && to && dayjs(to).isValid()) {
			return day.isBetween(from, to, "day", "[]");
		}
		return false;
	}

	async function changeCurrentScreen(screen: null | "month" | "year") {
		if (screen === "month") {
			await tick();
			const monthSelector = document.querySelector(".month-selection");
			const activeMonth = monthSelector.querySelector(".active");
			if (activeMonth) {
				activeMonth.scrollIntoView({
					behavior: "instant",
					block: "nearest"
				});
			}
		} else if (screen === "year") {
			await tick();
			const yearSelector = document.querySelector(".year-selection");
			const activeYear = yearSelector.querySelector(".active");
			if (activeYear) {
				activeYear.scrollIntoView({
					behavior: "instant",
					block: "nearest"
				});
			}
		} else {
			currentScreen = null;
		}
	}

	$: {
		value;
		if (dayjs(value).isValid()) {
			selected = dayjs.utc(value);
		}
	}

	$: {
		const yearDiff = maxDate.diff(minDate, "year");
		let extraYears = yearDiff % 4;
		if (extraYears !== 0) {
			extraYears = 4 - extraYears;
		}
		const totalYears = yearDiff + extraYears;

		years = Array(totalYears || 1)
			.fill(0)
			.map((_, i) => {
				const year = minDate.add(i, "year").year();
				return year;
			});
	}

	function getInitialSelected() {
		const today = dayjs.utc().startOf("day");
		const min = dayjs.utc(minDate).startOf("day");
		const max = dayjs.utc(maxDate).startOf("day");
		if (today.isBefore(min)) {
			return min;
		} else if (today.isAfter(max)) {
			return max;
		} else {
			return today;
		}
	}

	// Component state
	let selected = getInitialSelected();
	let currentScreen: null | "month" | "year" = null;
	let years: number[];

	const daysOfWeek = writable<string[]>([]);
	const calendarGrid = writable<Dayjs[][]>([]);

	function selectDate(date: Dayjs) {
		selected = date;
		dispatch("change", date.format("YYYY-MM-DD"));
	}

	$: setLocale(locale);

	const setLocale = async (locale: string) => {
		if (
			locale !== "en" &&
			typeof localeModules[`/node_modules/dayjs/esm/locale/${locale}.js`] !==
				"undefined"
		) {
			const module: any =
				await localeModules[`/node_modules/dayjs/esm/locale/${locale}.js`]();
			dayjs.locale(locale, module.default);
			selected = selected.locale(locale);
		}
	};

	const isDayAllowed = (day: Dayjs) => {
		const dayOfWeek = day.format("d");
		return allowedDays[dayOfWeek as keyof typeof allowedDays];
	};

	function updateCalendarGrid() {
		daysOfWeek.set(
			Array(7)
				.fill(0)
				.map((_, i) => dayjs.utc().weekday(i).format("dd"))
		);
		const startOfMonth = selected.startOf("month");
		const endOfMonth = selected.endOf("month");
		const firstDayInCalendar = startOfMonth.startOf("week");
		const lastDayInCalendar = endOfMonth.endOf("week");

		const days: Dayjs[] = [];

		for (
			let day = firstDayInCalendar;
			day.isSameOrBefore(lastDayInCalendar);
			day = day.add(1, "day")
		) {
			days.push(day);
		}

		calendarGrid.set(
			Array(Math.ceil(days.length / 7))
				.fill(0)
				.map(() => days.splice(0, 7))
		);
	}

	$: {
		selected;
		updateCalendarGrid();
	}
</script>

<div class="calendar">
	<div
		style="{currentScreen !== null ? 'opacity: 0' : ''}"
		class="calendar-transition">
		<div class="date-header">
			<button
				type="button"
				style="width: 2.5rem; height: 2.5rem;"
				class="date-atom date-atom-darker"
				class:disabled="{selected.format('MM-YYYY') === firstMonth}"
				disabled="{selected.format('MM-YYYY') === firstMonth}"
				on:click|preventDefault="{() =>
					(selected = selected.subtract(1, 'month'))}">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="21"
					height="21"
					viewBox="0 0 24 24"
					><path
						fill="none"
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="m14 7l-5 5l5 5"></path
					></svg>
			</button>
			<div style="display: flex">
				<button
					type="button"
					style="height: 2.5rem; padding: 0 0.5rem; text-transform: capitalize;"
					class="capitalize date-atom date-atom-darker"
					on:click|preventDefault="{() => {
						currentScreen = 'month';
					}}">
					{selected.locale(locale).format("MMMM")}
				</button>
				<button
					type="button"
					style="height: 2.5rem; padding: 0 0.5rem; text-transform: capitalize;"
					class="date-atom date-atom-darker"
					on:click="{() => {
						currentScreen = 'year';
					}}">
					{selected.locale(locale).format("YYYY")}
				</button>
			</div>
			<button
				type="button"
				style="width: 2.5rem; height: 2.5rem;"
				class="date-atom date-atom-darker"
				class:disabled="{selected.format('MM-YYYY') === lastMonth}"
				disabled="{selected.format('MM-YYYY') === lastMonth}"
				on:click="{() => (selected = selected.add(1, 'month'))}">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="21"
					height="21"
					viewBox="0 0 24 24"
					><path
						fill="none"
						stroke="currentColor"
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="m10 7l5 5l-5 5"></path
					></svg>
			</button>
		</div>
		<div class="datepicker-grid">
			{#each $daysOfWeek as day}
				<div class="day-week">{day}</div>
			{/each}
		</div>
		{#each $calendarGrid as week, i (i)}
			<div class="calendar-grid" transition:slide>
				{#each week as day}
					{@const highlight = shouldHighlight(day, highlightFrom, highlightTo)}
					{#if !day.isSame(selected, "month")}
						<div class="day"></div>
					{:else if day.isBefore(minDate) || day.isAfter(maxDate) || isDayAllowed(day) === false}
						<div class="day date-atom disabled">{day.format("D")}</div>
					{:else}
						<button
							type="button"
							on:click="{() => {
								selectDate(day);
							}}"
							class:highlight="{highlight}"
							class:active="{value &&
								dayjs(value).isValid() &&
								dayjs.utc(value).toISOString() === day.toISOString()}"
							class:underline="{day.isSame(dayjs(), 'day')}"
							class="day date-atom">
							{day.format("D")}
						</button>
					{/if}
				{/each}
			</div>
		{/each}
	</div>
	{#if currentScreen === "month"}
		<div
			transition:fade="{{ duration: 100 }}"
			class="absolute-cover month-selection">
			<div class="month-selection-grid">
				{#each Array(12).fill(0) as _, monthIndex}
					{#if (selected.year() === minDate.year() && monthIndex < minDate.month()) || (selected.year() === maxDate.year() && monthIndex > maxDate.month())}
						<div class="capitalize padding-normal disabled">
							{dayjs().locale(locale).month(monthIndex).format("MMM")}
						</div>
					{:else}
						<button
							type="button"
							class:active="{selected.month() === monthIndex}"
							class:underline="{monthIndex === dayjs().month()}"
							class="capitalize padding-normal date-atom year"
							on:click|preventDefault="{() => {
								selected = selected.month(monthIndex);
								currentScreen = null;
							}}">
							{dayjs().locale(locale).month(monthIndex).format("MMM")}
						</button>
					{/if}
				{/each}
			</div>
		</div>
	{:else if currentScreen === "year"}
		<div
			transition:fade="{{ duration: 100 }}"
			class="absolute-cover year-selection">
			<div class="year-selection-grid">
				{#each years as year}
					{#if year < minDate.year() || year > maxDate.year()}
						<div class="padding-normal disabled">{year}</div>
					{:else}
						<button
							type="button"
							class:active="{selected.year() === year}"
							class:underline="{year === dayjs().year()}"
							class="padding-normal date-atom month"
							on:click|preventDefault="{() => {
								selected = selected.year(year);
								currentScreen = null;
							}}">
							{year}
						</button>
					{/if}
				{/each}
			</div>
		</div>
	{/if}
</div>
